import { generatePath } from 'react-resource-router';

import type { BenchmarkRoutes } from '@atlassian/twg-benchmarking-experience/host-types';

/**
 * react-resource-router `name` and `path` values for microsite routes.
 * Keep these in sync with the route definitions in `@af/twg-microsite`.
 */
export const HOME_ROUTE_NAME = 'HOME';
export const CLI_ROUTE_NAME = 'CLI';
export const MCP_ROUTE_NAME = 'MCP';
export const BENCHMARK_ROUTE_NAME = 'BENCHMARK';
export const CONNECT_AGENTS_ROUTE_NAME = 'CONNECT_AGENTS';
export const BENCHMARK_RUN_ROUTE_NAME = 'BENCHMARK_RUN';
export const BENCHMARK_REVIEW_ROUTE_NAME = 'BENCHMARK_REVIEW';
export const BENCHMARK_REPORT_ROUTE_NAME = 'BENCHMARK_REPORT';

export const HOME_ROUTE_PATH = '/';
export const CLI_ROUTE_PATH = '/cli';
export const MCP_ROUTE_PATH = '/mcp';
export const BENCHMARK_ROUTE_PATH = '/benchmark';
export const CONNECT_AGENTS_ROUTE_PATH = '/connect-agents';

/**
 * A run and its report, addressed by the tenant that owns them and their id.
 *
 * The tenant is part of the path because a run belongs to one: the service
 * answers `404` for a run id read against any other tenant, so the id alone
 * does not identify a run. The benchmark waitlist route carries no tenant,
 * and the nav bar's site switcher already owns which site is selected.
 */
export const BENCHMARK_RUN_ROUTE_PATH = '/benchmark/:cloudId/runs/:runId';
/**
 * The blind claims review, which is a step of its own rather than a mode of the
 * run: it opens only once every prompt has finished, and its own address is
 * what lets a reload or a second tab return to a review in progress instead of
 * dropping back into a run that has nothing left to show.
 *
 * Nothing server-side enforces the order. The service has no review state: it
 * serves the report as soon as every prompt's report is stored, and the review
 * document is written separately by this app. So this address is a convention
 * of the flow, not a gate.
 */
export const BENCHMARK_REVIEW_ROUTE_PATH = '/benchmark/:cloudId/runs/:runId/review';
export const BENCHMARK_REPORT_ROUTE_PATH = '/benchmark/:cloudId/runs/:runId/report';

/**
 * Built with the router's own `generatePath` rather than by hand, so a URL can
 * only be assembled from the path it is declared with. It encodes each param
 * exactly as `encodeURIComponent` does, and refuses a param the pattern does
 * not accept rather than emitting an address that matches nothing.
 */
export const benchmarkRunUrl = (cloudId: string, runId: string): string =>
	generatePath(BENCHMARK_RUN_ROUTE_PATH, { cloudId, runId });

export const benchmarkReviewUrl = (cloudId: string, runId: string): string =>
	generatePath(BENCHMARK_REVIEW_ROUTE_PATH, { cloudId, runId });

export const benchmarkReportUrl = (cloudId: string, runId: string): string =>
	generatePath(BENCHMARK_REPORT_ROUTE_PATH, { cloudId, runId });

/** The benchmark's routes as the microsite mounts it. */
export const MICROSITE_BENCHMARK_ROUTES: BenchmarkRoutes = {
	routeNames: {
		run: BENCHMARK_RUN_ROUTE_NAME,
		review: BENCHMARK_REVIEW_ROUTE_NAME,
		report: BENCHMARK_REPORT_ROUTE_NAME,
	},
	setupPath: BENCHMARK_ROUTE_PATH,
	runUrl: benchmarkRunUrl,
	reviewUrl: benchmarkReviewUrl,
	reportUrl: benchmarkReportUrl,
};

export type MicrositeRouteName =
	| typeof HOME_ROUTE_NAME
	| typeof CLI_ROUTE_NAME
	| typeof MCP_ROUTE_NAME
	| typeof BENCHMARK_ROUTE_NAME
	| typeof CONNECT_AGENTS_ROUTE_NAME
	| typeof BENCHMARK_RUN_ROUTE_NAME
	| typeof BENCHMARK_REVIEW_ROUTE_NAME
	| typeof BENCHMARK_REPORT_ROUTE_NAME;

const MICROSITE_ROUTES: readonly { name: MicrositeRouteName; path: string }[] = [
	{ name: HOME_ROUTE_NAME, path: HOME_ROUTE_PATH },
	{ name: CLI_ROUTE_NAME, path: CLI_ROUTE_PATH },
	{ name: MCP_ROUTE_NAME, path: MCP_ROUTE_PATH },
	{ name: BENCHMARK_ROUTE_NAME, path: BENCHMARK_ROUTE_PATH },
	{ name: CONNECT_AGENTS_ROUTE_NAME, path: CONNECT_AGENTS_ROUTE_PATH },
	{ name: BENCHMARK_RUN_ROUTE_NAME, path: BENCHMARK_RUN_ROUTE_PATH },
	{ name: BENCHMARK_REVIEW_ROUTE_NAME, path: BENCHMARK_REVIEW_ROUTE_PATH },
	{ name: BENCHMARK_REPORT_ROUTE_NAME, path: BENCHMARK_REPORT_ROUTE_PATH },
];

/**
 * Narrows a router-supplied route name to one this app defines.
 *
 * The router types `name` as a plain string and the table carries a catch-all
 * that redirects, so a name off the router is not necessarily one of these.
 */
export const isMicrositeRouteName = (name: string | undefined): name is MicrositeRouteName =>
	MICROSITE_ROUTES.some((route) => route.name === name);

/**
 * Whether a route carries a run, and so a tenant, in its path.
 *
 * True of the run, its review and its report, false of benchmark waitlist. What it really
 * answers is whether the URL stops making sense when the selected site changes.
 */
export const isBenchmarkRunRouteName = (name: string | undefined): boolean =>
	name === BENCHMARK_RUN_ROUTE_NAME ||
	name === BENCHMARK_REVIEW_ROUTE_NAME ||
	name === BENCHMARK_REPORT_ROUTE_NAME;

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** A route path as a whole-pathname test, each `:param` standing for one segment. */
const routePathPattern = (path: string): RegExp =>
	new RegExp(
		`^${path
			.split('/')
			.map((segment) => (segment.startsWith(':') ? '[^/]+' : escapeRegExp(segment)))
			.join('/')}$`,
	);

const MICROSITE_ROUTE_PATTERNS: readonly { name: MicrositeRouteName; pattern: RegExp }[] =
	MICROSITE_ROUTES.map(({ name, path }) => ({ name, pattern: routePathPattern(path) }));

/**
 * The route a pathname is, exactly, or `undefined` when it is not one.
 *
 * Deliberately not the router's own matching, which is case-insensitive and
 * tolerates a trailing slash: it answers "would the app serve this?", and the
 * app serves every path, since an unmatched one redirects to home. The
 * question here is the stricter "is this one of our canonical paths?", because
 * the answer decides whether a URL is trusted over the `<head>` of the
 * document the gateway delivered. The router's `matchPath` does take `strict`
 * and `sensitive`, but neither the package nor its route matcher exposes them,
 * so `/CLI` and `/cli/` would both be taken as `/cli` and could render one
 * route's page beneath another route's metadata.
 */
export const micrositeRouteNameForPath = (pathname: string): MicrositeRouteName | undefined =>
	MICROSITE_ROUTE_PATTERNS.find(({ pattern }) => pattern.test(pathname))?.name;
